/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import map from 'lodash/map';
import usePlacesAutocomplete, { getGeocode, getLatLng } from 'use-places-autocomplete';
import { MyLocation } from '@yme/assets/icons';
import { AutocompleteRequest } from '@yme/atoms/autocomplete-request';
import { BottomSheet, Header } from '@yme/atoms/bottom-sheet';
import { LabelStyled } from '@yme/atoms/input';
import { SpinnerLoaderSizes } from '@yme/atoms/spinner-loader';
import { SVGIcon } from '@yme/atoms/svg-icon';
import { useGeolocation } from '@yme/hooks/useGeolocation';
import { useWindowSize } from '@yme/hooks/useWindowSize';
import { RegularTextLevel4 } from '@yme/tokens/typography';
import { withGoogleApi } from '@yme/utils/withGoogleApi';
import { DEFAULT_PLACEHOLDER, MAX_ADDITIONAL_COUNTRIES_LENGTH } from './constants';
import { getAutocompletePredictions } from './services';
import { BottomSheetAutocompleteContainerStyled, ContainerStyled, NonPointerEventsContainerStyled, SpinnerLoaderStyled, } from './styled';
const PlacesAutocompleteComponent = memo(({ title, placeholder = DEFAULT_PLACEHOLDER, countries, additionalCountries = [], isRightToLeft, value: incomingValue, hasError = false, errorMessage = '', isRequired = false, showTitleAboveInput = true, onMyGeolocationPress, onMyGeolocationObtain, onOptionSelect, onClear, onBlur, }) => {
    const [inputError, setInputError] = useState(null);
    const [isBottomSheetOpened, setIsBottomSheetOpened] = useState(false);
    const [additionalPredictions, setAdditionalPredictions] = useState([]);
    const { isInfiniteDesktopView: isDesktop } = useWindowSize();
    const { value, setValue, suggestions: { data }, clearSuggestions, } = usePlacesAutocomplete({
        requestOptions: {
            componentRestrictions: {
                country: countries ?? null,
            },
        },
        debounce: 300,
        defaultValue: incomingValue,
    });
    useEffect(() => {
        if (value && additionalCountries.length) {
            getAutocompletePredictions({
                input: value,
                countries: additionalCountries.slice(0, MAX_ADDITIONAL_COUNTRIES_LENGTH),
            }).then((data) => {
                setAdditionalPredictions(data);
            });
        }
    }, [additionalCountries, value]);
    useEffect(() => {
        setValue(incomingValue ?? '');
    }, [incomingValue, setValue]);
    const onGeolocationSuccess = useCallback(({ latitude, longitude }) => {
        const coordinates = {
            lat: latitude,
            lng: longitude,
        };
        const label = `${latitude}, ${longitude}`;
        const location = {
            coordinates,
            label,
        };
        setValue(label);
        onOptionSelect?.(location);
        onMyGeolocationObtain?.(location);
    }, [onMyGeolocationObtain, onOptionSelect, setValue]);
    const onGeolocationFail = useCallback(({ message }) => {
        setInputError({
            message,
        });
    }, []);
    // todo: md: rename onGetGeolocation -> getGeolocation
    const { isRequestingGeolocation, onGetGeolocation } = useGeolocation(onGeolocationSuccess, onGeolocationFail);
    const handleInputChange = useCallback((value) => {
        setValue(value);
        setInputError(null);
    }, [setValue]);
    const handleOptionSelect = useCallback(({ label }) => {
        setValue(label, false);
        getGeocode({
            address: label,
        }).then((results) => {
            const { lat, lng } = getLatLng(results[0]);
            const coordinates = {
                lat,
                lng,
            };
            onOptionSelect?.({
                coordinates,
                label,
            });
        });
    }, [onOptionSelect, setValue]);
    const dismissBottomSheet = useCallback(() => {
        setIsBottomSheetOpened(false);
    }, []);
    const openBottomSheet = useCallback(() => {
        setIsBottomSheetOpened(true);
    }, []);
    const handleOptionSelectMobile = useCallback((value) => {
        handleOptionSelect(value);
        dismissBottomSheet();
    }, [dismissBottomSheet, handleOptionSelect]);
    const handleInputClear = useCallback(() => {
        clearSuggestions();
        setValue('');
        onClear?.(null);
    }, [clearSuggestions, onClear, setValue]);
    const handleGetGeolocation = useCallback(() => {
        setValue('');
        clearSuggestions();
        onGetGeolocation();
        onMyGeolocationPress?.();
    }, [clearSuggestions, onGetGeolocation, onMyGeolocationPress, setValue]);
    const suggestionList = useMemo(() => map([...data, ...additionalPredictions], ({ place_id, description }) => ({
        value: place_id,
        label: description,
    })), [additionalPredictions, data]);
    const ControlButtons = useMemo(() => isRequestingGeolocation ? (React.createElement(SpinnerLoaderStyled, { isLoading: true, size: SpinnerLoaderSizes.SMALL })) : (React.createElement(SVGIcon, { type: MyLocation, onClick: handleGetGeolocation })), [handleGetGeolocation, isRequestingGeolocation]);
    return (React.createElement(ContainerStyled, { isRightToLeft: isRightToLeft },
        showTitleAboveInput && (React.createElement(RegularTextLevel4, { weight: 500 },
            React.createElement(LabelStyled, { isRequired: isRequired }, title))),
        isDesktop ? (React.createElement(AutocompleteRequest, { isRightToLeft: isRightToLeft, value: value, suggestionList: suggestionList, placeholder: placeholder, ControlButtons: ControlButtons, hasError: Boolean(inputError) || hasError, infoText: errorMessage, onChange: handleInputChange, onSelect: handleOptionSelect, onClear: handleInputClear, onBlur: onBlur })) : (React.createElement(React.Fragment, null,
            React.createElement("div", { onClick: openBottomSheet, role: "button" },
                React.createElement(NonPointerEventsContainerStyled, null,
                    React.createElement(AutocompleteRequest, { isRightToLeft: isRightToLeft, value: value, suggestionList: [], placeholder: placeholder, ControlButtons: ControlButtons, hasError: Boolean(inputError) || hasError, infoText: inputError?.message ?? errorMessage, onBlur: onBlur }))),
            React.createElement(BottomSheet, { open: isBottomSheetOpened, onDismiss: dismissBottomSheet, header: React.createElement(Header, { headerLeft: title }) },
                React.createElement(BottomSheetAutocompleteContainerStyled, { isRightToLeft: isRightToLeft },
                    React.createElement(AutocompleteRequest, { isRightToLeft: isRightToLeft, value: value, suggestionList: suggestionList, placeholder: placeholder, ControlButtons: ControlButtons, hasError: Boolean(inputError) || hasError, infoText: inputError?.message ?? errorMessage, onChange: handleInputChange, onSelect: handleOptionSelectMobile, onClear: handleInputClear, onBlur: onBlur })))))));
});
PlacesAutocompleteComponent.displayName = 'PlacesAutocomplete';
export const PlacesAutocomplete = withGoogleApi(PlacesAutocompleteComponent);
